import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Lock, Unlock, Plus, FileText, Fingerprint, Trash2, Settings2, ShieldCheck, PenLine, ChevronLeft, Image as ImageIcon, Mic, Video } from "lucide-react";
import { playTap, playSuccess } from "@/lib/sounds";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { AttachmentView } from "@/components/AttachmentView";
import { PaywallSheet } from "@/components/PaywallSheet";
import { ChangePinForm, PinRecovery } from "@/components/VaultSave";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errorMessage, isPaywallError, absUrl } from "@/lib/api";
import { setVaultToken, clearVaultToken, getVaultToken, vaultHeaders, biometricSupported, registerBiometric, unlockWithBiometric, acceptFor, ACCEPT_BY_CATEGORY, FILE_LABELS } from "@/lib/vault";
import { relativeDate } from "@/lib/format";

function PinInput({ value, onChange, label, testId, autoFocus }) {
  return (
    <label className="block text-xs font-medium ga-muted">{label}
      <input type="password" inputMode="numeric" autoComplete="off" autoFocus={autoFocus} value={value} onChange={(e) => onChange(e.target.value)} className="ga-input mt-1 text-center text-2xl tracking-[0.5em]" maxLength={32} data-testid={testId} />
    </label>
  );
}

function Gate({ info, onUnlocked, initialRecover = false }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [bio, setBio] = useState(false);
  const [recover, setRecover] = useState(initialRecover && info.exists);
  useEffect(() => { biometricSupported().then(setBio); }, []);
  const creating = !info.exists;
  if (recover) return <PinRecovery info={info} onDone={onUnlocked} onCancel={() => setRecover(false)} />;

  const submit = async (e) => {
    e.preventDefault();
    if (creating && pin !== confirm) return toast.error("Os PINs não coincidem.");
    playTap();
    setBusy(true);
    try {
      const { data } = creating ? await api.post("/vault", { pin }) : await api.post("/vault/unlock", { pin });
      if (!creating) playSuccess();
      onUnlocked(data);
      if (creating) toast.success("Pasta Segura criada.");
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const useBio = async () => {
    try { onUnlocked(await unlockWithBiometric()); } catch (err) { toast.error(err?.response ? errorMessage(err) : "Biometria não reconhecida. Use o PIN."); }
  };

  return (
    <form onSubmit={submit} className="ga-fade-up px-5" data-testid={creating ? "vault-create-form" : "vault-unlock-form"}>
      <div className="flex flex-col items-center py-8 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-3xl" style={{ background: "#05324A" }}>{creating ? <ShieldCheck size={36} color="#33D9B2" /> : <Lock size={36} color="#33D9B2" />}</div>
        <h2 className="mt-5 text-xl font-semibold">{creating ? "Crie sua Pasta Segura" : "Pasta Segura bloqueada"}</h2>
        <p className="ga-muted mt-1 max-w-xs text-sm">{creating ? "Defina um PIN (4 a 8 dígitos) ou senha. Só quem tiver o PIN vê o conteúdo — nem prévias, nem buscas, nem notificações." : "Digite seu PIN para ver o conteúdo."}</p>
      </div>
      <div className="space-y-3">
        <PinInput label={creating ? "Novo PIN" : "PIN"} value={pin} onChange={setPin} testId="vault-pin-input" autoFocus />
        {creating && <PinInput label="Confirme o PIN" value={confirm} onChange={setConfirm} testId="vault-pin-confirm-input" />}
        <button type="submit" disabled={busy || pin.length < 4} className="ga-btn-primary w-full" data-testid="vault-submit-button">{busy ? "Aguarde..." : creating ? "Criar Pasta Segura" : "Desbloquear"}</button>
        {!creating && info.biometric_enabled && bio && (
          <button type="button" onClick={useBio} className="ga-btn-ghost w-full" data-testid="vault-biometric-button"><Fingerprint size={18} /> Usar biometria</button>
        )}
        {!creating && <button type="button" onClick={() => setRecover(true)} className="ga-muted w-full text-center text-xs underline" data-testid="vault-forgot-pin">Esqueci meu PIN</button>}
      </div>
      {creating && <p className="ga-muted mt-6 text-center text-xs">O conteúdo é criptografado com uma chave protegida pelo seu PIN. Se esquecer o PIN, não será possível recuperar o conteúdo.</p>}
    </form>
  );
}

export default function Vault() {
  const location = useLocation();
  const [info, setInfo] = useState(null);
  const [unlocked, setUnlocked] = useState(() => !!getVaultToken());
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [text, setText] = useState({ title: "", content: "" });
  const [paywall, setPaywall] = useState(null);
  const [bioAvail, setBioAvail] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const fileRef = useRef(null);

  const lock = useCallback((silent) => {
    clearVaultToken(); setUnlocked(false); setItems([]); setOpen(null); clearTimeout(timer.current);
    if (!silent) toast("Pasta Segura bloqueada.");
  }, []);

  const armTimer = useCallback(() => {
    clearTimeout(timer.current);
    const minutes = info?.auto_lock_minutes || 5;
    timer.current = setTimeout(() => lock(), minutes * 60 * 1000);
  }, [info?.auto_lock_minutes, lock]);

  const loadInfo = useCallback(() => api.get("/vault").then((r) => setInfo(r.data)).catch((e) => toast.error(errorMessage(e))), []);
  useEffect(() => { loadInfo(); biometricSupported().then(setBioAvail); }, [loadInfo]);
  const leaveTimer = useRef(null);
  useEffect(() => {
    clearTimeout(leaveTimer.current);
    return () => { leaveTimer.current = setTimeout(clearVaultToken, 0); clearTimeout(timer.current); };
  }, []);
  useEffect(() => {
    if (!unlocked) return;
    const events = ["click", "keydown", "touchstart", "scroll"];
    events.forEach((ev) => window.addEventListener(ev, armTimer, { passive: true }));
    const onHide = () => document.hidden && lock(true);
    document.addEventListener("visibilitychange", onHide);
    armTimer();
    return () => { events.forEach((ev) => window.removeEventListener(ev, armTimer)); document.removeEventListener("visibilitychange", onHide); };
  }, [unlocked, armTimer, lock]);

  const request = async (fn) => {
    try { return await fn(); } catch (e) {
      if (e?.response?.status === 423) { lock(true); toast.error(errorMessage(e)); }
      else if (isPaywallError(e)) setPaywall(errorMessage(e));
      else toast.error(errorMessage(e));
      throw e;
    }
  };
  const loadItems = useCallback(() => request(() => api.get("/vault/items", { headers: vaultHeaders() })).then((r) => setItems(r.data)).catch(() => {}), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (getVaultToken()) loadItems(); }, [loadItems]);

  const onUnlocked = (data) => { setVaultToken(data.vault_token); setInfo((i) => ({ ...i, ...data, exists: true })); setUnlocked(true); setTimeout(loadItems, 0); };

  const saveText = async () => {
    setBusy(true);
    try {
      if (open?.id) await request(() => api.put(`/vault/items/${open.id}`, text, { headers: vaultHeaders() }));
      else await request(() => api.post("/vault/items", text, { headers: vaultHeaders() }));
      toast.success("Guardado na Pasta Segura."); setDialog(null); setOpen(null); setText({ title: "", content: "" }); loadItems();
    } catch {} finally { setBusy(false); }
  };
  const uploadFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    e.target.value = "";
    setBusy(true);
    const fd = new FormData(); fd.append("file", f); fd.append("title", f.name);
    try { await request(() => api.post("/vault/items/upload", fd, { headers: vaultHeaders() })); toast.success("Arquivo guardado na Pasta Segura."); loadItems(); } catch {} finally { setBusy(false); }
  };
  const openItem = async (it) => {
    if (it.type === "text") { const { data } = await request(() => api.get(`/vault/items/${it.id}`, { headers: vaultHeaders() })); setOpen(data); setText({ title: data.title, content: data.content }); setDialog("text"); }
    else setOpen(it);
  };
  const removeItem = async (it) => { await request(() => api.delete(`/vault/items/${it.id}`, { headers: vaultHeaders() })); toast.success("Item excluído."); setOpen(null); loadItems(); };
  const toggleBio = async () => {
    try {
      if (info.biometric_enabled) { await request(() => api.delete("/vault/biometric", { headers: vaultHeaders() })); toast.success("Biometria desativada."); }
      else { await registerBiometric(); toast.success("Biometria ativada para desbloquear a Pasta Segura."); }
      loadInfo();
    } catch (e) { if (!e?.response) toast.error("Não foi possível ativar a biometria neste aparelho."); }
  };
  const setAutoLock = async (m) => { await request(() => api.put("/vault/settings", { auto_lock_minutes: Number(m) }, { headers: vaultHeaders() })); setInfo((i) => ({ ...i, auto_lock_minutes: Number(m) })); };

  if (!info) return <div className="px-5 pt-8"><div className="ga-card h-40 animate-pulse" /></div>;
  if (!unlocked || !getVaultToken()) return (<div className="pb-8"><PageHeader title="Pasta Segura" /><Gate info={info} onUnlocked={onUnlocked} initialRecover={!!location.state?.recover} /></div>);

  const allowed = info.allowed_categories || ["image", "audio"];

  if (open && open.type !== "text") {
    return (
      <div className="pb-8">
        <PageHeader title={open.title} right={<button onClick={() => removeItem(open)} className="ga-surface flex h-10 w-10 items-center justify-center rounded-full text-red-500" aria-label="Excluir" data-testid="vault-item-delete-button"><Trash2 size={18} /></button>} />
        <div className="px-5" data-testid="vault-item-view">
          <button onClick={() => setOpen(null)} className="ga-muted mb-3 flex items-center gap-1 text-xs" data-testid="vault-item-back"><ChevronLeft size={14} /> Voltar para a pasta</button>
          <AttachmentView file={open.file} url={absUrl(open.file_url)} testId="vault-attachment" />
        </div>
      </div>
    );
  }

  const pickFile = (cat) => {
    setDialog(null);
    if (!allowed.includes(cat)) return setPaywall("Documentos e vídeos na Pasta Segura estão disponíveis no plano Premium.");
    if (fileRef.current) { fileRef.current.accept = ACCEPT_BY_CATEGORY[cat]; fileRef.current.click(); }
  };
  const ADD_OPTIONS = [["image", "Foto", ImageIcon], ["audio", "Áudio", Mic], ["document", "Documento", FileText], ["video", "Vídeo", Video]];

  return (
    <div className="pb-8">
      <PageHeader title="Pasta Segura" right={<div className="flex gap-2"><button onClick={() => { playTap(); setDialog("settings"); }} className="ga-surface flex h-10 w-10 items-center justify-center rounded-full" aria-label="Configurações da Pasta Segura" data-testid="vault-settings-button"><Settings2 size={18} /></button><button onClick={() => { playTap(); lock(); }} className="ga-surface flex h-10 w-10 items-center justify-center rounded-full" aria-label="Bloquear agora" data-testid="vault-lock-button"><Unlock size={18} /></button></div>} />
      <div className="ga-fade-up px-5" data-testid="vault-unlocked">
        <div className="rounded-3xl p-4 text-white" style={{ background: "linear-gradient(135deg,#05324A,#0A6373)" }}>
          <div className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck size={18} color="#33D9B2" /> Desbloqueada</div>
          <p className="mt-1 text-xs text-white/75">Bloqueio automático em {info.auto_lock_minutes} min sem uso, ao sair da pasta ou ao minimizar o app.</p>
        </div>
        <button onClick={() => { playTap(); setDialog("add"); }} disabled={busy} className="ga-btn-primary mt-4 w-full text-base" data-testid="vault-add-button"><Plus size={20} /> {busy ? "Enviando..." : "Adicionar à Pasta Segura"}</button>
        <input ref={fileRef} type="file" accept={acceptFor(allowed)} onChange={uploadFile} className="hidden" data-testid="vault-file-input" />
        <p className="ga-muted mt-2 text-[11px]">{info.is_premium ? "Textos, fotos, áudios, PDF, Word, PowerPoint, planilhas e vídeos." : "Textos, fotos e áudios no plano Free. Documentos e vídeos no Premium."} O espaço usado conta no armazenamento do seu plano.</p>

        <div className="mt-5 space-y-2.5" data-testid="vault-items-list">
          {items.length === 0 && <div className="ga-card p-8 text-center text-sm" data-testid="vault-empty-state"><p className="font-medium">Sua Pasta Segura está vazia.</p><p className="ga-muted mt-1 text-xs">Guarde aqui o que precisa de privacidade extra.</p></div>}
          {items.map((it) => (
            <div key={it.id} className="ga-card flex items-center gap-3 p-3" data-testid={`vault-item-${it.id}`}>
              <button onClick={() => openItem(it)} className="flex min-w-0 flex-1 items-center gap-3 text-left" data-testid={`vault-item-open-${it.id}`}>
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl" style={{ background: "#05324A22", color: "#05324A" }}>{it.type === "text" ? <FileText size={20} /> : <Lock size={20} />}</div>
                <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold">{it.title}</div><div className="ga-muted text-xs">{FILE_LABELS[it.type] || "Arquivo"} • {relativeDate(it.created_at)}</div></div>
              </button>
              <button onClick={() => removeItem(it)} className="p-2 text-red-400" aria-label="Excluir" data-testid={`vault-item-delete-${it.id}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={dialog === "add"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="vault-add-dialog">
          <DialogTitle>Adicionar à Pasta Segura</DialogTitle>
          <DialogDescription className="text-sm">O que você quer guardar? Tudo fica criptografado e fora da busca.</DialogDescription>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => { playTap(); setDialog("text"); setOpen(null); setText({ title: "", content: "" }); }} className="flex h-20 flex-col items-center justify-center gap-1.5 rounded-2xl text-sm font-semibold text-white" style={{ background: "#7657E8" }} data-testid="vault-add-text-button"><PenLine size={20} /> Texto</button>
            {ADD_OPTIONS.map(([cat, label, Icon]) => (
              <button key={cat} onClick={() => { playTap(); pickFile(cat); }} className="relative flex h-20 flex-col items-center justify-center gap-1.5 rounded-2xl text-sm font-semibold text-white" style={{ background: allowed.includes(cat) ? "#13C9A3" : "#9AA5B1" }} data-testid={`vault-add-${cat}-button`}>
                <Icon size={20} /> {label}
                {!allowed.includes(cat) && <span className="absolute right-2 top-2 rounded-full bg-white/90 px-1.5 py-0.5 text-[9px] font-bold uppercase" style={{ color: "#05324A" }}>Premium</span>}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "text"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="vault-text-dialog">
          <DialogTitle>{open?.id ? "Texto seguro" : "Novo texto seguro"}</DialogTitle>
          <DialogDescription className="sr-only">Conteúdo criptografado</DialogDescription>
          <input value={text.title} onChange={(e) => setText({ ...text, title: e.target.value })} placeholder="Título" className="ga-input" data-testid="vault-text-title-input" />
          <textarea value={text.content} onChange={(e) => setText({ ...text, content: e.target.value })} rows={6} placeholder="Conteúdo" className="ga-input resize-none" data-testid="vault-text-content-input" />
          <div className="flex gap-3">
            {open?.id && <button onClick={() => { removeItem(open); setDialog(null); }} className="ga-btn-ghost text-red-500" data-testid="vault-text-delete-button"><Trash2 size={16} /></button>}
            <button onClick={saveText} disabled={busy || !text.title.trim()} className="ga-btn-primary flex-1" data-testid="vault-text-save-button">Salvar</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "settings"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="vault-settings-dialog">
          <DialogTitle>Configurações da Pasta Segura</DialogTitle>
          <DialogDescription className="sr-only">Bloqueio automático e biometria</DialogDescription>
          <label className="block text-xs ga-muted">Bloquear automaticamente após
            <select value={info.auto_lock_minutes} onChange={(e) => setAutoLock(e.target.value)} className="ga-input mt-1 py-2.5" data-testid="vault-autolock-select">
              {[1, 2, 5, 10, 15, 30].map((m) => <option key={m} value={m}>{m} min sem uso</option>)}
            </select>
          </label>
          <div className="flex items-center gap-3 text-sm">
            <Fingerprint size={18} style={{ color: "#0A6373" }} />
            <div className="flex-1"><div className="font-medium">Desbloquear com biometria</div><div className="ga-muted text-xs">{bioAvail ? "Face ID, Touch ID ou digital do aparelho." : "Não disponível neste aparelho/navegador."}</div></div>
            <button onClick={toggleBio} disabled={!bioAvail && !info.biometric_enabled} className="ga-btn-ghost px-3 py-2 text-xs" data-testid="vault-biometric-toggle">{info.biometric_enabled ? "Desativar" : "Ativar"}</button>
          </div>
          <ChangePin onDone={() => setDialog(null)} />
        </DialogContent>
      </Dialog>

      <PaywallSheet open={!!paywall} reason={paywall} onClose={() => setPaywall(null)} />
    </div>
  );
}

function ChangePin({ onDone }) {
  const [show, setShow] = useState(false);
  if (!show) return <button onClick={() => setShow(true)} className="ga-muted text-left text-xs underline" data-testid="vault-change-pin-toggle">Alterar PIN</button>;
  return <ChangePinForm onDone={onDone} />;
}
